import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchDeviceList, resetDeviceList, storeUnenrolledDevices } from "../../actions/SelectDevice";
import { getLocalSessionData, getUserSessionData } from "../../utils/Authentication/handleUserSession";
import { BMC_ENROLMENT_TYPE, ECOPROTOKEN_CLAIMS, LOCALSESSIONDATAKEYS, refreshBtn } from "../../constants/AppConstants";
import ErrorPage from "../shared/errorPage/ErrorPage";
import Loader from "../shared/loader/Loader";
import { Redirect, useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { DEEP_LINK_PARAMS, routePath } from "../../constants/Routes";
import PaymentComplete from "../PaymentComplete/PaymentComplete";
import { useTranslation } from "react-i18next";
import { Is_BrotherPlus_Signup, isDesktopOrBmcClient } from "../../constants/AppMethod";
import { UserEnrolledDevicesServiceInit } from "../../store/UserEnrolledDevices/UserEnrolledDevicesSlice";

export const USE_NOT_ENROLLED_DEVICE_LIST = 'USE_NOT_ENROLLED_DEVICE_LIST';

const BMCDeviceEnrollmentCheck = (props) => {
    const { t } = useTranslation(["translation"]);
    const history = useHistory();
    const IsBrotherPlus_Signup = Is_BrotherPlus_Signup();
    const isNativeApp = isDesktopOrBmcClient();
    const { 
        source = BMC_ENROLMENT_TYPE.BMC_AS_ENROLLMENT_SERVICE, 
        RouterLinkStateProp, 
        IsUpgradeToEcoPro, 
        subscriptionKind = null
    } = props?.location?.state || props;
    const dispatch = useDispatch();
    const UID = getUserSessionData(ECOPROTOKEN_CLAIMS.USERID);
    const [devicesListLength, setDevicesListLength] = useState(0);
    const [isValidateEnrollment, canValidateEnrollment] = useState(false);
    const deviceListResult = useSelector((state) => state.SelectDevice);
    const {
        userDeviceList_error,
        userDeviceList_success,
        userDeviceList_data
    } = useSelector(state => state.ROOTREDUCER_RTK.GetUserEnrolledDevicesService);

    const REDIRECTION_LINKS = {
        'DEVICE_LENGTH_ONE': (source === BMC_ENROLMENT_TYPE.BMC_AS_POST_ENROLLMENT_SERVICE)
            ? { pathname: routePath.CORRECT_DEVICE_FOUND, state: {subscriptionKind} }
            : (devicesListLength > 0)
                ? {
                    pathname: routePath.PLANS,
                    state: {
                        'deviceId': deviceListResult.deviceList?.devices[0]?.deviceId,
                        'device_model': deviceListResult.deviceList?.devices[0]?.model,
                        'serialNumber': deviceListResult.deviceList?.devices[0]?.serialNumber,
                        'networkStatus': deviceListResult.deviceList?.devices[0]?.networkStatus,
                        'deviceLength': "1"
                    }
                } : {},
        'DEVICE_LENGTH_MORE_THAN_ONE': (source === BMC_ENROLMENT_TYPE.BMC_AS_POST_ENROLLMENT_SERVICE)
            ? { pathname: routePath.MORE_DEVICE_FOUND, state: {subscriptionKind} }
            : routePath.CONFIRM_DEVICE
    }

    useEffect(() => {
        if (!userDeviceList_success) {
            dispatch(UserEnrolledDevicesServiceInit({ doEnrolmentCheck: true, doDeviceExistCheck: true }));
        }
    }, [dispatch, userDeviceList_success])

    useEffect(() => {
        // reset devicelistinfo API state
        dispatch(resetDeviceList());

        if (userDeviceList_success && !userDeviceList_data?.isDashboard) {
            const enrolledDevices = userDeviceList_data?.userEnrolledDeviceViewModel || [];
            const isEnrolledSubscriber = enrolledDevices.length > 0;
            const pathHistory = JSON.parse(sessionStorage.getItem('USER_PATH_HISTORY'));
            const storedPath = pathHistory?.path || '';
            const storedUrl = new URL(storedPath, window.location.origin);
            const isDeepLinkPath = storedUrl.searchParams.has(DEEP_LINK_PARAMS.DEVICE_ID)
                                    && storedUrl.searchParams.has(DEEP_LINK_PARAMS.SECTION);
            const hasDeepLink = isEnrolledSubscriber
                && isDeepLinkPath
                && pathHistory?.isSubscribed === true
                && source === BMC_ENROLMENT_TYPE.BMC_AS_ENROLLMENT_SERVICE
                && !IsUpgradeToEcoPro
                && RouterLinkStateProp !== 'SUBSCRIPTION_NETWORK_ERR_VALIDATION';
            if (!hasDeepLink) {
                dispatch(fetchDeviceList(UID));
            }
        }
        // eslint-disable-next-line
    }, [
        dispatch,
        UID,
        userDeviceList_success,
        IsBrotherPlus_Signup,
        IsUpgradeToEcoPro,
        history
    ]);

    /*
    * Edge case handling: 
    * The payment API (ManageSubscription and PlaceOrderAsync) may fail due to network issues.
    * However, the enrollment process might still be completed successfully on the server.
    * To handle this edge case:
    *   1. We validate the enrollment status independently from the payment response.
    *   2. If the enrollment is confirmed, we redirect the user to the success page.
    *   3. This avoids confusion and ensures the user sees the correct flow even if payment status is unclear.
    */
    const handleEnrollmentValidation = () => {
        let targetDeviceId = getLocalSessionData(LOCALSESSIONDATAKEYS.DEVICE_ID);
        const isDeviceEnrolled = Object.values(deviceListResult.deviceList.devices)
            .filter((val) => {
                return (
                    val.deviceId === targetDeviceId
                    && val.hasOwnProperty('service')
                    && val.service
                ) ? val.service.currentPlan : "";
            })
        return (isDeviceEnrolled.length > 0) ? true : false;
    }

    const handleMultipleDeviceList = useCallback(() => {
        const deviceListInfoAPIInfo = deviceListResult.deviceList?.devices;

        if (IsUpgradeToEcoPro) {
            let deviceId = getLocalSessionData(LOCALSESSIONDATAKEYS.DEVICE_ID);
            let ecoproUpgradeDeviceDetails = deviceListInfoAPIInfo.find((item) => item.deviceId === deviceId);
            history.push({
                pathname: routePath.PLANS,
                state: {
                    'deviceId': ecoproUpgradeDeviceDetails?.deviceId,
                    'device_model': ecoproUpgradeDeviceDetails?.model,
                    'serialNumber': ecoproUpgradeDeviceDetails?.serialNumber,
                    'networkStatus': ecoproUpgradeDeviceDetails?.networkStatus,
                    'deviceLength': "1"
                }
            });
        } else {
            const userEnroledDeviceAPIInfo = userDeviceList_data?.userEnrolledDeviceViewModel;
            const devicesNotEnroledList = deviceListInfoAPIInfo.filter(
                item => !userEnroledDeviceAPIInfo.some(f => f.deviceId === item.deviceId)
            );
            
            dispatch(storeUnenrolledDevices(devicesNotEnroledList));
            if (devicesNotEnroledList?.length > 1) {
                history.push(REDIRECTION_LINKS.DEVICE_LENGTH_MORE_THAN_ONE);
            } else {
                history.push((source === BMC_ENROLMENT_TYPE.BMC_AS_POST_ENROLLMENT_SERVICE)
                    ? {
                        pathname: routePath.CORRECT_DEVICE_FOUND,
                        state: { 'type': USE_NOT_ENROLLED_DEVICE_LIST, subscriptionKind}
                    }
                    : (devicesNotEnroledList?.length > 0)
                        ? {
                            pathname: routePath.PLANS,
                            state: {
                                'deviceId': devicesNotEnroledList[0]?.deviceId,
                                'device_model': devicesNotEnroledList[0]?.model,
                                'serialNumber': devicesNotEnroledList[0]?.serialNumber,
                                'networkStatus': devicesNotEnroledList[0]?.networkStatus,
                                'deviceLength': "1"
                            }
                        } : {})
            }
        }
        // eslint-disable-next-line
    }, [
        deviceListResult,
        dispatch,
        history,
        userDeviceList_data,
        source
    ]);

    useEffect(() => {
        if (deviceListResult.success && deviceListResult.deviceList?.devices.length > 0) {
            const deviceCount = deviceListResult.deviceList?.devices.length;
            setDevicesListLength(deviceCount);
            if (RouterLinkStateProp && RouterLinkStateProp === 'SUBSCRIPTION_NETWORK_ERR_VALIDATION') {
                canValidateEnrollment(true);
            } else if (deviceCount > 1) {
                handleMultipleDeviceList();
            }
        }
        // eslint-disable-next-line
    }, [
        deviceListResult.success,
        deviceListResult.deviceList?.devices,
        handleMultipleDeviceList
    ]);


    // this event hook will be triggered when an error eccors in payemnt page and user clicks on retry button
    // please keep this code block independent and do not add additional dependency to the event.
    useEffect(() => {
        if (RouterLinkStateProp && RouterLinkStateProp === 'SUBSCRIPTION_NETWORK_ERR_VALIDATION') {
            if (userDeviceList_success) {
                dispatch(fetchDeviceList(UID));
            }
        }
        // eslint-disable-next-line
    }, [RouterLinkStateProp]);

    if (deviceListResult.error) {
        return (
            <ErrorPage
                errorInfo={deviceListResult.error}
                showRefreshBtn={refreshBtn.HIDE}
            ></ErrorPage>
        );
    }

    if (deviceListResult.success && devicesListLength === 0) {
        return (
            <ErrorPage
                errorInfo={t("ERROR_CONTENT.NO_DEVICES")}
                showRefreshBtn={refreshBtn.HIDE}
            ></ErrorPage>
        );
    }

    if (userDeviceList_error) {
        return <ErrorPage errorInfo={userDeviceList_error}></ErrorPage>
    }

    if (isValidateEnrollment) {
        const isCurrentDeviceEnrolled = handleEnrollmentValidation();
        return (isCurrentDeviceEnrolled)
            ? <PaymentComplete isSubscriptionCompleted={true}></PaymentComplete>
            : <ErrorPage errorInfo={t("ERROR_CONTENT.ERR_INFO.SUBSCRIPTION_NOT_ACTIVATED")}></ErrorPage>
    }

    if (
        userDeviceList_success
        && !IsUpgradeToEcoPro
        && RouterLinkStateProp !== 'SUBSCRIPTION_NETWORK_ERR_VALIDATION'
        && source === BMC_ENROLMENT_TYPE.BMC_AS_ENROLLMENT_SERVICE
    ) {
        const pathHistory = JSON.parse(sessionStorage.getItem('USER_PATH_HISTORY'));
        if (pathHistory?.path && pathHistory?.isSubscribed === true) {
            return <Redirect to={pathHistory['path']} />
        }
    }

    if (
        userDeviceList_success
        && userDeviceList_data?.isDashboard
        && !IsUpgradeToEcoPro
        && RouterLinkStateProp !== 'SUBSCRIPTION_NETWORK_ERR_VALIDATION'
    ) {
       return <Redirect to={(isNativeApp) ? routePath.MY_DEVICES : routePath.DASHBOARD} />
    }

    if (deviceListResult.success && devicesListLength === 1) {
        return <Redirect to={REDIRECTION_LINKS.DEVICE_LENGTH_ONE} />
    }

    return (
        <Loader />
    )
}

export default BMCDeviceEnrollmentCheck;